import { Injectable } from "@angular/core";
import { Action, State, StateContext } from "@ngxs/store";
import { produce } from "immer";
import { Observable } from "rxjs";
import { tap } from "rxjs/operators";
import { Router } from "@angular/router";
import { ApplicationPaths } from "../../constants/application-paths";

import { SignInCommand } from "../models/sign-in-command";
import { SignInResult, SignInStatus } from "../models/sign-in-result";
import { SignInService } from "../services/sign-in.service";
import { SignIn } from "./sign-in.actions";

export interface SignInStateModel {
    signIn: {
        form: {
            model: SignInCommand;
            dirty: boolean;
            status: string;
            errors: never;
        };
        result: SignInResult;
    };
}

@Injectable()
@State<SignInStateModel>({ name: "signIn" })
export class SignInState {
    constructor(
        private signInService: SignInService,
        private router: Router
    ) { }

    @Action(SignIn)
    public signIn(ctx: StateContext<SignInStateModel>, action: SignIn): Observable<SignInResult> {
        return this.signInService.signIn(action.payload).pipe(tap((result) => {
            ctx.setState(produce(ctx.getState(), (draft) => {
                draft.signIn = draft.signIn ?? {
                    form: undefined,
                    result: undefined,
                };

                draft.signIn.result = result;
            }));

            if (result.status === SignInStatus.Success) {
                // Check if user needs MFA setup
                if (result.requiresMfaSetup || (result.mustEnableTwoFactor && !result.twoFactorEnabled)) {
                    // Redirect to MFA setup page
                    this.router.navigate([ApplicationPaths.MfaSetup]);
                } else if (action.payload.returnUrl) {
                    location.href = action.payload.returnUrl;
                } else {
                    location.href = "/";
                }
            } else if (result.status === SignInStatus.TwoFactorSetupRequired) {
                // Handle new status 7 - Two Factor Setup Required

                // Store the original sign-in credentials for continuation after MFA setup
                sessionStorage.setItem("mfa-setup-credentials", JSON.stringify(action.payload));

                this.router.navigate([ApplicationPaths.MfaSetup], {
                    queryParams: { Id: result.userId }
                });
            } else if (result.status === SignInStatus.TwoFactorVerificationRequired) {
                // Handle new status 8 - Two Factor Verification Required

                // Store the original sign-in credentials for continuation after MFA verification
                sessionStorage.setItem("mfa-verification-credentials", JSON.stringify(action.payload));

                this.router.navigate([ApplicationPaths.MfaVerification], {
                    queryParams: { Id: result.userId, userEmail: result.userEmail }
                });
            }
        }));
    }
}
